import { Component, Show, createUniqueId } from "solid-js";

export interface InputProps {
  id?: string;
  name?: string;
  type?: "text" | "email" | "password" | "number" | "search";
  placeholder?: string;
  value?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  maxLength?: number;
  isTextarea?: boolean;
  rows?: number;
  class?: string;
  autofocus?: boolean;
  onInput?: (value: string) => void;
  onBlur?: () => void;
}

export const Input: Component<InputProps> = (props) => {
  const generatedId = createUniqueId();
  const inputId = () => props.id ?? generatedId;
  const errorId = () => `${inputId()}-error`;
  const hasError = () => !!props.error;
  const showCharCount = () => props.maxLength !== undefined;
  const charCount = () => (props.value ?? "").length;

  const baseClasses =
    "block w-full rounded border px-3 text-sm transition-colors focus-ring " +
    "disabled:bg-sunken disabled:text-ink-3 disabled:cursor-not-allowed";

  const stateClasses = () =>
    hasError()
      ? "border-signal-red text-ink placeholder:text-ink-3"
      : "border-line bg-surface text-ink placeholder:text-ink-3";

  const inputClasses = () =>
    baseClasses + " h-7 py-0 " + stateClasses() + " " + (props.class ?? "");

  const textareaClasses = () =>
    baseClasses +
    " py-1.5 resize-none " +
    stateClasses() +
    " " +
    (props.class ?? "");

  const handleInput = (
    e: InputEvent & { currentTarget: HTMLInputElement | HTMLTextAreaElement },
  ) => {
    props.onInput?.(e.currentTarget.value);
  };

  return (
    <div class="w-full">
      <Show when={props.label}>
        <label
          for={inputId()}
          class="block text-sm font-medium text-ink-2 mb-1"
        >
          {props.label}
        </label>
      </Show>

      <Show
        when={props.isTextarea}
        fallback={
          <input
            id={inputId()}
            name={props.name}
            type={props.type ?? "text"}
            placeholder={props.placeholder}
            value={props.value ?? ""}
            disabled={props.disabled}
            maxLength={props.maxLength}
            autofocus={props.autofocus}
            class={inputClasses()}
            aria-invalid={hasError()}
            aria-describedby={hasError() ? errorId() : undefined}
            onInput={handleInput}
            onBlur={() => props.onBlur?.()}
          />
        }
      >
        <textarea
          id={inputId()}
          name={props.name}
          placeholder={props.placeholder}
          value={props.value ?? ""}
          disabled={props.disabled}
          maxLength={props.maxLength}
          rows={props.rows ?? 3}
          class={textareaClasses()}
          aria-invalid={hasError()}
          aria-describedby={hasError() ? errorId() : undefined}
          onInput={handleInput}
          onBlur={() => props.onBlur?.()}
        />
      </Show>

      <div class="flex justify-between mt-1">
        <Show when={hasError()}>
          <p id={errorId()} class="text-sm text-signal-red">
            {props.error}
          </p>
        </Show>
        <Show when={!hasError()}>
          <span />
        </Show>
        <Show when={showCharCount()}>
          <span class="text-xs text-ink-3">
            {charCount()}/{props.maxLength}
          </span>
        </Show>
      </div>
    </div>
  );
};
